import {
  Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy,
  Output, SimpleChanges, ViewChild
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { computePdfRenderSize } from './pdf-render-scale';

/** Version-matched worker served from our own origin (see main.ts). */
const WORKER_SRC = '/assets/pdf.worker.min.js';
/** If no page is drawn by then, show the Open/Share/Download fallback instead of a blank area. */
export const FIRST_PAGE_TIMEOUT_MS = 8000;

/**
 * Renders PDF bytes inline as canvases with pdf.js.
 *
 * We never put PDFs in an <iframe>/<embed>: iOS Safari shows a blank frame (or
 * only page 1, unscrollable) for blob: PDFs. Canvas rendering looks the same on
 * iPhone Safari, desktop Chrome and the iOS app web view, and the bytes never
 * leave the page, so private files stay behind the authenticated API.
 */
@Component({
  selector: 'app-pdf-canvas-viewer',
  template: `
    <div class="pdf-canvas-status" *ngIf="rendering && !hasFailed">Loading PDF…</div>
    <div #pages class="pdf-canvas-pages" [attr.aria-label]="label" role="document"></div>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .pdf-canvas-status { padding: 24px; text-align: center; color: rgba(0, 0, 0, 0.6); }
    .pdf-canvas-pages { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 0 0 16px; }
  `]
})
export class PdfCanvasViewerComponent implements OnChanges, OnDestroy {
  /** Raw PDF bytes. A copy is handed to pdf.js (its worker detaches buffers). */
  @Input() data: ArrayBuffer;
  @Input() label = 'PDF document';
  /** Show the fallback if page 1 isn't drawn by then (slow/unsupported device). */
  @Input() firstPageTimeoutMs = FIRST_PAGE_TIMEOUT_MS;
  /** Emits the page count once every page is drawn. */
  @Output() rendered = new EventEmitter<number>();
  /** Emits when pdf.js cannot open or draw the file (show a fallback). */
  @Output() failed = new EventEmitter<any>();

  @ViewChild('pages') pagesEl: ElementRef<HTMLDivElement>;

  rendering = false;
  hasFailed = false;

  private doc: any = null;
  private renderToken = 0;
  private lastWidth = 0;
  private resizeTimer: any = null;
  private firstPageTimer: any = null;
  private firstPageDrawn = false;
  private readonly onResize = () => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      const width = this.availableWidth();
      // Only redraw for real width changes (not the iOS URL-bar height jiggle).
      if (this.doc && Math.abs(width - this.lastWidth) > 24) {
        this.drawAll(this.doc, ++this.renderToken);
      }
    }, 250);
  };

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {
    const lib: any = pdfjsLib;
    if (lib && lib.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) {
      lib.GlobalWorkerOptions.workerSrc = (window as any).pdfWorkerSrc || WORKER_SRC;
    }
    this.zone.runOutsideAngular(() => window.addEventListener('resize', this.onResize));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.load();
    }
  }

  ngOnDestroy() {
    this.renderToken++;
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.firstPageTimer);
    this.destroyDoc();
  }

  private load() {
    const token = ++this.renderToken;
    this.destroyDoc();
    this.clearPages();
    this.hasFailed = false;
    if (!this.data || this.data.byteLength === 0) {
      this.rendering = false;
      return;
    }
    this.rendering = true;
    this.firstPageDrawn = false;
    clearTimeout(this.firstPageTimer);
    this.zone.runOutsideAngular(() => {
      this.firstPageTimer = setTimeout(() => {
        if (token === this.renderToken && !this.firstPageDrawn) {
          this.renderToken++; // abandon the slow render
          this.fail(new Error('PDF render timed out'));
        }
      }, this.firstPageTimeoutMs);
    });
    const bytes = new Uint8Array(this.data.slice(0));
    const lib: any = pdfjsLib;
    let task: any;
    try {
      task = lib.getDocument({ data: bytes });
    } catch (err) {
      this.fail(err);
      return;
    }
    task.promise.then((doc: any) => {
      if (token !== this.renderToken) {
        doc.destroy();
        return;
      }
      this.doc = doc;
      return this.drawAll(doc, token);
    }).catch((err: any) => {
      if (token === this.renderToken) {
        this.fail(err);
      }
    });
  }

  private drawAll(doc: any, token: number): Promise<void> {
    const container = this.pagesEl && this.pagesEl.nativeElement;
    if (!container) {
      return Promise.resolve();
    }
    const width = this.availableWidth();
    this.lastWidth = width;
    const ratio = window.devicePixelRatio || 1;
    const canvases: HTMLCanvasElement[] = [];
    let chain: Promise<any> = Promise.resolve();
    for (let n = 1; n <= doc.numPages; n++) {
      chain = chain.then(() => {
        if (token !== this.renderToken) {
          return;
        }
        return doc.getPage(n).then((page: any) => {
          if (token !== this.renderToken) {
            return;
          }
          const base = page.getViewport({ scale: 1 });
          const size = computePdfRenderSize(base.width, base.height, width, ratio);
          const viewport = page.getViewport({ scale: size.scale });
          const canvas = document.createElement('canvas');
          canvas.className = 'pdf-canvas-page';
          canvas.width = size.pixelWidth;
          canvas.height = size.pixelHeight;
          canvas.style.width = size.cssWidth + 'px';
          canvas.style.height = size.cssHeight + 'px';
          canvas.style.maxWidth = '100%';
          canvas.style.background = '#fff';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
          canvas.setAttribute('aria-label', 'Page ' + n + ' of ' + doc.numPages);
          const ctx = canvas.getContext('2d');
          canvases.push(canvas);
          return page.render({ canvasContext: ctx, viewport }).promise.then(() => {
            if (n === 1 && token === this.renderToken) {
              this.firstPageDrawn = true;
              clearTimeout(this.firstPageTimer);
            }
          });
        });
      });
    }
    return chain.then(() => {
      if (token !== this.renderToken) {
        return;
      }
      // Swap in all pages at once so a resize redraw never flashes blank.
      this.clearPages();
      canvases.forEach(c => container.appendChild(c));
      this.zone.run(() => {
        this.rendering = false;
        this.rendered.emit(doc.numPages);
      });
    }).catch((err) => {
      if (token === this.renderToken) {
        this.fail(err);
      }
    });
  }

  private availableWidth(): number {
    const el = this.host.nativeElement;
    const w = el ? el.clientWidth : 0;
    // Leave a small gutter; cap so desktop pages stay a readable size.
    const usable = (w || window.innerWidth || 0) - 16;
    return Math.max(200, Math.min(usable, 1000));
  }

  private fail(err: any) {
    clearTimeout(this.firstPageTimer);
    this.destroyDoc();
    console.error('PDF render failed:', err);
    this.zone.run(() => {
      this.rendering = false;
      this.hasFailed = true;
      this.failed.emit(err);
    });
  }

  private clearPages() {
    const container = this.pagesEl && this.pagesEl.nativeElement;
    if (container) {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }
  }

  private destroyDoc() {
    if (this.doc) {
      try { this.doc.destroy(); } catch (e) { /* ignore */ }
      this.doc = null;
    }
  }
}
